import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { FilterMetadata, LazyLoadEvent, MenuItem, SortEvent, SortMeta } from 'primeng/api';
import { Table } from 'primeng/table';

import { Memoize } from '@core/decorators';
import { NoResultsOptions } from '@core/interfaces';
import { EventService, GridActions, GridService, WindowService } from '@core/services';

import { SelectRowEvent, SelectedRow } from '#types/grid';

@Component({
  selector: 'z-grid-table',
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  @Input() gridId: string;
  @Input() dataKey: string = null;
  @Input() trackByColumn: string = null;

  @Input() sorts: SortMeta[];
  @Input() filters: { [s: string]: FilterMetadata | FilterMetadata[] };

  @Input() selectionMode: string = 'single';

  @Input() rowSelection: SelectedRow<any>;
  @Output() rowSelectionChange = new EventEmitter<SelectedRow<any>>();

  @Input() contextMenuItems: MenuItem[];
  @Input() contextMenuSelection: SelectedRow<any>;
  @Output() contextMenuSelectionChange = new EventEmitter<SelectedRow<any>>();

  @Input() onLazyLoad: (event: LazyLoadEvent) => void;
  @Input() onColReorder: (event: SortEvent) => void;
  @Input() onColResize: (event: any) => void;
  @Input() onPage: (event: any) => void;
  @Input() onRowSelect: (event: any) => void;
  @Input() onRowUnselect: (event: any) => void;
  @Input() onSort: (event: any) => void;
  @Input() onFilter: (event: any) => void;
  @Input() onContextMenuSelect: (event: any) => void;
  @Input() onRowEditInit: (data: any, index: number) => void;
  @Input() onRowEditSave: (data: any, index: number) => void;
  @Input() onRowEditCancel: (data: any, index: number) => void;

  @Output() selectRow = new EventEmitter<SelectRowEvent>();

  @Input() enableRowEditing: boolean = false;
  @Input() editMode: string = 'row';
  @Input() deferredRowEditingSave: boolean = false;

  @Input() rowEditingEnableConditionColumn: string;
  @Input() rowEditingEnableConditionValue: string;

  @Input() paginator: boolean = true;
  @Input() showCurrentPageReport: boolean = true;
  @Input() showPageLinks: boolean = true;
  @Input() pageLinks: number = 5;
  @Input() rowsPerPageOptions: number[] = [10, 20, 25, 50, 100];

  @Input() lazy: boolean = true;
  @Input() lazyLoadOnInit: boolean = false;

  @Input() noResultsComponentOptions: NoResultsOptions = {};

  @ViewChild('table') table: Table;

  wrapper: ElementRef<HTMLDivElement>;

  rowEditInit$ = new Subject<{ index: number; data: any }>();
  rowEditSave$ = new Subject<{ index: number; data: any }>();
  rowEditCancel$ = new Subject<{ index: number; data: any }>();

  loading$: Observable<boolean>;
  gridHeight$ = new BehaviorSubject<string>(null);
  rowsPerPageOptions$ = new BehaviorSubject<number[]>(null);
  showFirstLastIcon$ = new BehaviorSubject<boolean>(true);
  pageLinks$ = new BehaviorSubject<number>(this.pageLinks);

  constructor(
    public gridService: GridService,
    private store: Store,
    private renderer: Renderer2,
    private windowService: WindowService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    this.loading$ = this.eventService.loading(this.gridId);

    this.gridService.editSelectedRow$
      .pipe(
        filter((id) => id === this.gridId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.gridService
          .selectedRow$(this.gridId)
          .pipe(take(1))
          .subscribe((row) => {
            this.onRowEditInitHandler(row.data, row.index);
            this.table.initRowEdit(row.data);
          });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit() {
    this.wrapper = this.table.wrapperViewChild;

    // clear row selection when clicking on the background
    this.wrapper.nativeElement.addEventListener('click', (event) => {
      // workaround for primeng bug where table selection is broken
      // when multiple selection is enabled
      if (this.selectionMode === 'multiple') {
        if (!this.table.selection || !Array.isArray(this.table.selection)) {
          this.table.selection = [];
        }
      }

      if (this.wrapper.nativeElement === event.target) {
        this.table.toggleRowsWithCheckbox(null, false);
        this.store.dispatch(GridActions.ClearSelectedRow({ id: this.gridId }));
        this.store.dispatch(GridActions.ClearMultipleRows({ id: this.gridId }));
      }
    });

    // handle right click on the grid background
    this.wrapper.nativeElement.addEventListener('contextmenu', (event) => {
      if (this.wrapper.nativeElement === event.target) {
        this.table.toggleRowsWithCheckbox(null, false);
        this.store.dispatch(GridActions.ClearSelectedRow({ id: this.gridId }));
        this.store.dispatch(GridActions.ClearMultipleRows({ id: this.gridId }));

        this.table.contextMenu.show(event);
        this.wrapper.nativeElement.focus();
        event.preventDefault();
      }
    });

    this.gridService.height$
      .pipe(
        filter((event) => !!event && event.id === this.gridId),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.renderer.setStyle(this.wrapper.nativeElement, 'height', event.payload);
      });

    this.windowService.mobile$.pipe(takeUntil(this.destroy$)).subscribe((mobile) => {
      if (mobile) {
        this.rowsPerPageOptions$.next(null);
        this.showFirstLastIcon$.next(false);
        this.pageLinks$.next(null);
      } else {
        this.rowsPerPageOptions$.next(this.rowsPerPageOptions);
        this.showFirstLastIcon$.next(true);
        this.pageLinks$.next(this.pageLinks);
      }
    });

    this.windowService.viewportHeight$.pipe(takeUntil(this.destroy$)).subscribe((height) => {
      height = height - 240;

      this.gridHeight$.next(`${height}px`);
    });

    this.rowsPerPageOptions$.next(this.rowsPerPageOptions);
  }

  lazyLoadHandler(event: any) {
    if (this.onLazyLoad) {
      this.onLazyLoad(event);
    }
  }

  colReorderHandler(event: any) {
    this.gridService
      .availableColumns$(this.gridId)
      .pipe(take(1))
      .subscribe((availableColumns) => {
        this.store.dispatch(GridActions.SetColumns({ id: this.gridId, selectedColumns: event.columns, availableColumns }));
      });

    if (this.onColReorder) {
      this.onColReorder(event);
    }
  }

  colResizeHandler(event: any) {
    const column: string = event.element.getAttribute('data-field');
    const width: number = event.element.offsetWidth;
    const delta: number = event.delta;

    this.store.dispatch(GridActions.SetColumnSize({ id: this.gridId, column, size: width + delta }));

    if (this.onColResize) {
      this.onColResize(event);
    }
  }

  pageHandler(event: any) {
    if (this.onPage) {
      this.onPage(event);
    }
  }

  rowSelectHandler(event: any) {
    if (this.onRowSelect) {
      this.onRowSelect(event);
    }

    if (this.selectionMode === 'multiple') {
      this.selectRow.emit({
        originalEvent: event.originalEvent,
        data: event.data,
        type: event.type,
        index: event.index,
        selectedRows: [...this.table._selection],
      });
    }
  }

  contextMenuSelectHandler(event: any) {
    if (this.onRowSelect) {
      this.onRowSelect(event);
    }

    if (this.selectionMode === 'multiple') {
      this.selectRow.emit({
        originalEvent: event.originalEvent,
        data: event.data,
        type: event.type,
        index: event.index,
        selectedRows: [...this.table._selection],
      });
    }
  }

  rowUnselectHandler(event: any) {
    this.store.dispatch(GridActions.ClearSelectedRow({ id: this.gridId }));
    this.store.dispatch(GridActions.ClearMultipleRows({ id: this.gridId }));

    if (this.onRowUnselect) {
      this.onRowUnselect(event);
    }
  }

  sortHandler(event: any) {
    if (this.onSort) {
      this.onSort(event);
    }
  }

  filterHandler(event: any) {
    if (this.onFilter) {
      this.onFilter(event);
    }
  }

  @Memoize()
  checkRowEditingCondition(column: string, matchValue: string, columnValue: string) {
    if (!column || !matchValue) {
      return true;
    }

    if (!columnValue || columnValue !== matchValue) {
      return false;
    } else {
      return true;
    }
  }

  onRowEditInitHandler(data: any, index: number) {
    this.rowEditInit$.next({ index, data });

    if (this.onRowEditInit) {
      this.onRowEditInit(data, index);
    }
  }

  rowEditSave(index: number, data: any, datakey: string) {
    this.rowEditSave$.next({ index, data: null });

    const row = this.table.tableViewChild.nativeElement.querySelectorAll('tr.p-selectable-row')[index];

    this.table.saveRowEdit(data, row);
    delete this.table.editingRowKeys[datakey];

    this.table.cd.detectChanges();
  }

  onRowEditSaveHandler(data: any, index: number, row: HTMLTableRowElement) {
    if (!this.deferredRowEditingSave) {
      this.rowEditSave$.next({ index, data });

      this.table.saveRowEdit(data, row);
    }

    if (this.onRowEditSave) {
      this.onRowEditSave(data, index);
    }
  }

  onRowEditCancelHandler(data: any, index: number) {
    this.rowEditCancel$.next({ index, data });

    if (this.onRowEditCancel) {
      this.onRowEditCancel(data, index);
    }
  }

  rowTrackBy(_index: number, row: any) {
    if (!this.trackByColumn) {
      return row;
    }

    return row[this.trackByColumn];
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key?.toLowerCase() === 'a' && event.ctrlKey) {
      this.table.clearSelectionRange(null);
      this.table.anchorRowIndex = this.table.first;
      this.table.selectRange(null, this.table.first + this.table.rows);
    }
  }
}
